import React, { useState, useEffect, useCallback } from "react";
import { MapContainer, TileLayer, Marker, Popup, FeatureGroup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import 'leaflet-draw/dist/leaflet.draw.css';

// Fix for default marker icons in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const SidewalkRampMap = ({ onMapClick, selectedLocation }) => {
  const [map, setMap] = useState(null);
  const [featureGroup, setFeatureGroup] = useState(null);
  const [drawMode, setDrawMode] = useState("marker");
  const [EditControl, setEditControl] = useState(null);

  useEffect(() => {
    import('react-leaflet-draw').then((module) => {
      setEditControl(() => module.EditControl);
    });
  }, []);

  useEffect(() => {
    if (map && selectedLocation?.lat && selectedLocation?.lng) {
      map.flyTo([selectedLocation.lat, selectedLocation.lng], 16);
    }
  }, [selectedLocation, map]);

  const handleCreated = useCallback((e) => {
    const { layerType, layer } = e;
    if (layerType === "marker") {
      const latlng = layer.getLatLng();
      onMapClick({ lat: latlng.lat, lng: latlng.lng });
    }
  }, [onMapClick]);

  const handleMapClick = useCallback((e) => {
    if (drawMode === "marker") {
      onMapClick({
        lat: e.latlng.lat,
        lng: e.latlng.lng
      });
    }
  }, [drawMode, onMapClick]);

  const clearShapes = () => {
    if (featureGroup) {
      featureGroup.clearLayers();
    }
  };

  if (!EditControl) return <div>Loading map...</div>;

  return (
    <div style={{ height: "100%", width: "100%", position: "relative" }}>
      <div style={{
        position: "absolute",
        top: "10px",
        right: "10px",
        zIndex: 1000,
        display: "flex",
        gap: "10px",
        background: "white",
        padding: "10px",
        borderRadius: "4px",
        boxShadow: "0 2px 4px rgba(0,0,0,0.2)"
      }}>
        <button 
          onClick={() => setDrawMode("marker")}
          style={{ 
            background: drawMode === "marker" ? "#4CAF50" : "#f0f0f0",
            color: drawMode === "marker" ? "white" : "black"
          }}
        >
          Marker
        </button>
        <button 
          onClick={() => setDrawMode("line")}
          style={{ 
            background: drawMode === "line" ? "#4CAF50" : "#f0f0f0",
            color: drawMode === "line" ? "white" : "black"
          }}
        >
          Line
        </button>
        <button 
          onClick={() => setDrawMode("polygon")}
          style={{ 
            background: drawMode === "polygon" ? "#4CAF50" : "#f0f0f0",
            color: drawMode === "polygon" ? "white" : "black"
          }}
        >
          Polygon
        </button>
        <button onClick={clearShapes}>
          Clear
        </button>
      </div>

      <MapContainer
        center={[32.7157, -117.1611]}
        zoom={13}
        style={{ height: "100%", width: "100%" }}
        whenCreated={setMap}
        onClick={handleMapClick}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />

        <FeatureGroup ref={(ref) => setFeatureGroup(ref)}>
          <EditControl
            position="topright"
            onCreated={handleCreated}
            draw={{
              marker: drawMode === "marker",
              polyline: drawMode === "line",
              polygon: drawMode === "polygon",
              rectangle: false,
              circle: false,
              circlemarker: false
            }}
            edit={{
              edit: false,
              remove: false
            }}
          />
        </FeatureGroup>

        {selectedLocation && drawMode === "marker" && (
          <Marker 
            position={[selectedLocation.lat, selectedLocation.lng]}
            icon={new L.Icon({
              iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-gold.png',
              shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
              iconSize: [25, 41],
              iconAnchor: [12, 41],
              popupAnchor: [1, -34],
              shadowSize: [41, 41]
            })}
          >
            <Popup>Selected Location</Popup>
          </Marker>
        )}
      </MapContainer>

      <div style={{
        position: "absolute",
        bottom: "10px",
        left: "0",
        right: "0",
        textAlign: "center",
        color: "white",
        backgroundColor: "rgba(0,0,0,0.6)",
        padding: "8px",
        margin: "0 10px",
        borderRadius: "4px",
        fontSize: "14px",
        zIndex: 1000
      }}>
        {drawMode === "marker" 
          ? "Click on the map to place a marker" 
          : drawMode === "line" 
            ? "Click to start drawing a line" 
            : "Click to start drawing a polygon"}
      </div>
    </div>
  );
};

export default SidewalkRampMap;